import React, { useState } from 'react';
import { MapPin, Pencil, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { usePublicSettings, patchPublicSettings } from '../hooks/usePublicSettings';
import { useEditMode } from './InlineEditable';

/**
 * Extract lat/lng from any Google Maps URL and produce a stable embed src.
 * Accepts:
 *   - https://www.google.com/maps/place/.../@LAT,LNG,17z/...
 *   - https://maps.google.com/?q=LAT,LNG
 *   - https://goo.gl/maps/xyz         (falls back — cannot resolve without redirect)
 *   - https://www.google.com/maps/embed?pb=...  (pass-through)
 */
function toEmbedSrc(rawUrl) {
  if (!rawUrl) return null;
  const url = rawUrl.trim();
  // Already an embed URL — use as-is
  if (/\/maps\/embed\?pb=/.test(url)) return url;
  // Try @lat,lng pattern
  const at = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
  if (at) {
    const [, lat, lng] = at;
    return `https://maps.google.com/maps?q=${lat},${lng}&hl=tr&z=17&output=embed`;
  }
  // Try q=lat,lng pattern
  const q = url.match(/[?&]q=(-?\d+\.\d+),(-?\d+\.\d+)/);
  if (q) {
    const [, lat, lng] = q;
    return `https://maps.google.com/maps?q=${lat},${lng}&hl=tr&z=17&output=embed`;
  }
  // Otherwise: use raw URL as search query
  try {
    const u = new URL(url);
    if (u.host.includes('google') && u.pathname.startsWith('/maps')) {
      const search = url.split('/maps/')[1]?.split('/')[1] || '';
      if (search) return `https://maps.google.com/maps?q=${encodeURIComponent(decodeURIComponent(search))}&hl=tr&output=embed`;
    }
  } catch {
    // ignore
  }
  return null;
}

export default function GoogleMapEmbed({
  settingKey,
  fallback = '',
  height = 260,
  className = '',
}) {
  const canEdit = useEditMode();
  const settings = usePublicSettings();
  const [modalOpen, setModalOpen] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);

  const raw = settings[settingKey] || fallback || '';
  const embedSrc = toEmbedSrc(raw);

  const openModal = () => {
    setValue(raw);
    setModalOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const clean = (value || '').trim();
      await api.adminUpsertSetting(settingKey, clean);
      patchPublicSettings({ [settingKey]: clean });
      toast.success(clean ? 'Harita güncellendi' : 'Harita kaldırıldı');
      setModalOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kaydedilemedi');
    } finally {
      setSaving(false);
    }
  };

  // When no map set and reader is public, hide entire block
  if (!embedSrc && !canEdit) return null;

  return (
    <>
      <div className={`relative rounded-2xl overflow-hidden border border-ink-100 bg-cream-200 ${className}`} data-testid={`map-embed-${settingKey}`}>
        {embedSrc ? (
          <iframe
            src={embedSrc}
            width="100%"
            height={height}
            style={{ border: 0, display: 'block' }}
            loading="lazy"
            allowFullScreen
            referrerPolicy="no-referrer-when-downgrade"
            title="Google Haritalar"
          />
        ) : (
          <div className="flex flex-col items-center justify-center text-ink-400 text-sm gap-2" style={{ height }}>
            <MapPin className="w-6 h-6" />
            <span>Harita henüz eklenmedi</span>
            {canEdit && (
              <button onClick={openModal} className="mt-2 inline-flex items-center gap-1.5 text-xs h-8 px-3 rounded-full bg-ink-600 text-cream font-semibold hover:bg-ink-500">
                <Pencil className="w-3 h-3" /> Harita Ekle
              </button>
            )}
          </div>
        )}

        {canEdit && embedSrc && (
          <button
            type="button"
            onClick={openModal}
            data-testid={`map-edit-${settingKey}`}
            title="Haritayı düzenle"
            className="absolute top-2 right-2 inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-ink-600 text-cream text-xs font-semibold hover:bg-ink-500 shadow-lg"
          >
            <Pencil className="w-3 h-3" /> Haritayı Düzenle
          </button>
        )}

        {embedSrc && raw && !raw.includes('/embed?pb=') && (
          <a
            href={raw}
            target="_blank"
            rel="noopener noreferrer"
            className="absolute bottom-2 right-2 inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full bg-white/95 text-ink-600 text-[10px] font-bold uppercase tracking-widest shadow-md hover:bg-white"
          >
            Google Haritalar&apos;da Aç <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>

      {modalOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-ink-900/60 backdrop-blur-sm"
          onClick={(e) => { if (e.target === e.currentTarget) setModalOpen(false); }}
          data-testid={`map-edit-modal-${settingKey}`}
        >
          <div className="bg-white text-ink-600 rounded-3xl shadow-2xl w-full max-w-xl overflow-hidden">
            <div className="p-8">
              <div className="text-[11px] tracking-[0.3em] uppercase text-coral-300 font-bold">Konum</div>
              <h3 className="font-display text-3xl mt-2">Haritayı Düzenle</h3>
              <p className="mt-2 text-ink-400 text-sm leading-relaxed">
                Google Haritalar&apos;da konumu aç → <strong>Paylaş</strong> → <strong>Bağlantı</strong> sekmesindeki URL&apos;yi buraya yapıştır.
                (Veya <strong>Haritayı yerleştir</strong> altındaki HTML kodunun <code>src=&quot;...&quot;</code> URL&apos;sini de kabul eder.)
              </p>

              <div className="mt-6">
                <label className="text-[10px] tracking-widest uppercase text-ink-400 font-bold">Harita URL&apos;si</label>
                <textarea
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  data-testid={`map-edit-input-${settingKey}`}
                  placeholder="https://www.google.com/maps/place/..."
                  rows={4}
                  className="mt-2 w-full px-4 py-3 rounded-xl bg-cream-200 border border-ink-100 focus:border-ink-500 focus:ring-2 focus:ring-ink-500/10 outline-none text-ink-600 font-mono text-xs"
                />
                <div className="mt-2 text-[11px] text-ink-400 leading-relaxed">
                  Konum otomatik olarak koordinatlardan tespit edilir (@enlem,boylam formatı).
                  Boş bırakıp Kaydet dersen harita kaldırılır.
                </div>
              </div>
            </div>

            <div className="px-8 py-5 bg-cream-200 flex items-center justify-between gap-3">
              <a
                href="https://maps.google.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-ink-500 hover:text-ink-600 font-semibold inline-flex items-center gap-1"
              >
                Google Haritalar&apos;ı Aç <ExternalLink className="w-3 h-3" />
              </a>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="h-11 px-5 rounded-full text-ink-500 hover:text-ink-600 text-sm font-semibold"
                >
                  Vazgeç
                </button>
                <button
                  type="button"
                  onClick={save}
                  disabled={saving}
                  data-testid={`map-edit-save-${settingKey}`}
                  className="h-11 px-6 rounded-full bg-ink-600 text-cream text-sm font-bold hover:bg-ink-500 disabled:opacity-60"
                >
                  {saving ? 'Kaydediliyor…' : 'Kaydet'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
